/**
 * Guest mode — people/events in localStorage (same shapes as API responses).
 */
import { computeTileState, daysSinceLabel, formatShortDate } from "../growth.js";

const GUEST_FLAG = "growme_guest";
const STORE_KEY = "growme_guest_data";

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `g-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function dateOnly(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : new Date().toISOString().slice(0, 10);
}

function parsePlaces(place) {
  if (Array.isArray(place)) {
    return [...new Set(place.map((p) => String(p || "").trim()).filter(Boolean))];
  }
  if (place == null || place === "") return [];
  return [
    ...new Set(
      String(place)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

export function isGuest() {
  try {
    return localStorage.getItem(GUEST_FLAG) === "1";
  } catch {
    return false;
  }
}

export function enableGuest() {
  localStorage.setItem(GUEST_FLAG, "1");
  if (!localStorage.getItem(STORE_KEY)) {
    saveStore({ people: [], events: [] });
  }
}

export function clearGuestSession() {
  try {
    localStorage.removeItem(GUEST_FLAG);
  } catch {
    /* ignore */
  }
}

/** Clear guest flag + data (e.g. after leaving guest mode). */
export function clearGuestData() {
  try {
    localStorage.removeItem(GUEST_FLAG);
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { people: [], events: [] };
    const data = JSON.parse(raw);
    return {
      people: Array.isArray(data.people) ? data.people : [],
      events: Array.isArray(data.events) ? data.events : [],
    };
  } catch {
    return { people: [], events: [] };
  }
}

function saveStore(data) {
  localStorage.setItem(STORE_KEY, JSON.stringify(data));
}

function personStats(person, events) {
  const mine = events.filter((e) => (e.personIds || []).includes(person.id));
  const dates = mine.map((e) => e.date).filter(Boolean).sort();
  const lastMetAt = dates.length ? dates[dates.length - 1] : null;
  const firstMetAt = dates.length ? dates[0] : null;
  const eventCount = mine.length;
  const state = computeTileState({ lastMetAt, eventCount });

  const placeMap = new Map();
  for (const e of mine) {
    for (const name of e.places || []) {
      const prev = placeMap.get(name) || { name, eventCount: 0, lastDate: null };
      prev.eventCount += 1;
      if (!prev.lastDate || e.date > prev.lastDate) prev.lastDate = e.date;
      placeMap.set(name, prev);
    }
  }
  const places = [...placeMap.values()].sort((a, b) =>
    String(b.lastDate).localeCompare(String(a.lastDate)),
  );

  return {
    eventCount,
    lastMetAt,
    firstMetAt,
    state,
    sinceLast: daysSinceLabel(lastMetAt),
    firstMetLabel: formatShortDate(firstMetAt),
    places: places.slice(0, 8),
  };
}

function enrichPerson(person, events) {
  return {
    id: person.id,
    name: person.name,
    phone: person.phone || null,
    createdAt: person.createdAt,
    ...personStats(person, events),
  };
}

function enrichEvent(event, people) {
  const places = event.places || parsePlaces(event.place);
  return {
    id: event.id,
    date: event.date,
    note: event.note || "",
    place: places[0] || null,
    places,
    createdAt: event.createdAt,
    people: (event.personIds || [])
      .map((id) => people.find((p) => p.id === id))
      .filter(Boolean)
      .map((p) => ({ id: p.id, name: p.name })),
  };
}

function eventsForPerson(personId, events, people) {
  return events
    .filter((e) => (e.personIds || []).includes(personId))
    .map((e) => enrichEvent(e, people))
    .sort(
      (a, b) =>
        String(b.date).localeCompare(String(a.date)) ||
        String(b.createdAt).localeCompare(String(a.createdAt)),
    );
}

export const guestApi = {
  me: async () => ({ user: { id: "guest", email: null, displayName: "Guest" } }),

  garden: async () => {
    const { people, events } = loadStore();
    const list = people
      .map((p) => enrichPerson(p, events))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return {
      people: list,
      counts: {
        total: list.length,
        week: list.filter((p) => p.state === "week").length,
        month: list.filter((p) => p.state === "month").length,
        quarter: list.filter((p) => p.state === "quarter").length,
        year: list.filter((p) => p.state === "year").length,
        older: list.filter((p) => p.state === "older").length,
      },
    };
  },

  people: async (q = "") => {
    const { people, events } = loadStore();
    const query = String(q || "").trim().toLowerCase();
    let list = people.map((p) => enrichPerson(p, events));
    if (query) list = list.filter((p) => p.name.toLowerCase().includes(query));
    list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    return { people: list };
  },

  person: async (id) => {
    const { people, events } = loadStore();
    const row = people.find((p) => p.id === id);
    if (!row) {
      const err = new Error("Person not found");
      err.status = 404;
      throw err;
    }
    return {
      person: {
        ...enrichPerson(row, events),
        events: eventsForPerson(id, events, people),
      },
    };
  },

  createPerson: async ({ name, phone }) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) throw new Error("Please enter a name");
    const store = loadStore();
    const person = {
      id: uuid(),
      name: trimmed,
      phone: phone ? String(phone).trim() : null,
      createdAt: new Date().toISOString(),
    };
    store.people.push(person);
    saveStore(store);
    return { person: enrichPerson(person, store.events) };
  },

  updatePerson: async (id, { name, phone } = {}) => {
    const store = loadStore();
    const idx = store.people.findIndex((p) => p.id === id);
    if (idx < 0) {
      const err = new Error("Person not found");
      err.status = 404;
      throw err;
    }
    const existing = store.people[idx];
    const nextName =
      name !== undefined ? String(name || "").trim() : existing.name;
    if (!nextName) throw new Error("Please enter a name");
    const nextPhone =
      phone !== undefined
        ? phone
          ? String(phone).trim()
          : null
        : existing.phone;
    store.people[idx] = { ...existing, name: nextName, phone: nextPhone };
    saveStore(store);
    return {
      person: {
        ...enrichPerson(store.people[idx], store.events),
        events: eventsForPerson(id, store.events, store.people),
      },
    };
  },

  deletePerson: async (id) => {
    const store = loadStore();
    store.people = store.people.filter((p) => p.id !== id);
    // Unlink only — moments and places remain
    store.events = store.events.map((e) => ({
      ...e,
      personIds: (e.personIds || []).filter((pid) => pid !== id),
    }));
    saveStore(store);
    return { ok: true };
  },

  events: async ({ personId = null, place = null } = {}) => {
    const { people, events } = loadStore();
    let list = events.map((e) => enrichEvent(e, people));
    if (personId) list = list.filter((e) => e.people.some((p) => p.id === personId));
    if (place) list = list.filter((e) => (e.places || []).includes(place));
    list.sort(
      (a, b) =>
        String(b.date).localeCompare(String(a.date)) ||
        String(b.createdAt).localeCompare(String(a.createdAt)),
    );
    return { events: list };
  },

  createEvent: async ({ personIds, date, note, place }) => {
    const ids = [...new Set((personIds || []).map(String).filter(Boolean))];
    if (!ids.length) throw new Error("Select at least one person");
    const cleanedNote = String(note || "").trim();
    if (!cleanedNote) throw new Error("Note is required");
    const store = loadStore();
    for (const id of ids) {
      if (!store.people.some((p) => p.id === id)) throw new Error("One or more people were not found");
    }
    const places = parsePlaces(place);
    const event = {
      id: uuid(),
      date: dateOnly(date),
      note: cleanedNote,
      place: places[0] || null,
      places,
      personIds: ids,
      createdAt: new Date().toISOString(),
    };
    store.events.push(event);
    saveStore(store);
    return { event: enrichEvent(event, store.people) };
  },

  updateEvent: async (id, body) => {
    const store = loadStore();
    const idx = store.events.findIndex((e) => e.id === id);
    if (idx < 0) return { event: null };
    const existing = store.events[idx];
    const ids =
      body.personIds !== undefined
        ? [...new Set((body.personIds || []).map(String).filter(Boolean))]
        : existing.personIds;
    if (!ids.length) throw new Error("Select at least one person");
    const cleanedNote =
      body.note !== undefined ? String(body.note || "").trim() : existing.note;
    if (!cleanedNote) throw new Error("Note is required");
    const places =
      body.place !== undefined || body.places !== undefined
        ? parsePlaces(body.places != null ? body.places : body.place)
        : existing.places;
    store.events[idx] = {
      ...existing,
      date: body.date !== undefined ? dateOnly(body.date) : existing.date,
      note: cleanedNote,
      place: places[0] || null,
      places,
      personIds: ids,
    };
    saveStore(store);
    return { event: enrichEvent(store.events[idx], store.people) };
  },

  deleteEvent: async (id) => {
    const store = loadStore();
    store.events = store.events.filter((e) => e.id !== id);
    saveStore(store);
    return { ok: true };
  },

  places: async () => {
    const { people, events } = loadStore();
    const map = new Map();
    for (const e of events) {
      for (const name of e.places || []) {
        const prev = map.get(name) || {
          name,
          eventCount: 0,
          lastDate: null,
          people: new Map(),
        };
        prev.eventCount += 1;
        if (!prev.lastDate || e.date > prev.lastDate) prev.lastDate = e.date;
        for (const pid of e.personIds || []) {
          const p = people.find((x) => x.id === pid);
          if (p) prev.people.set(p.id, { id: p.id, name: p.name });
        }
        map.set(name, prev);
      }
    }
    const places = [...map.values()]
      .map((p) => ({
        name: p.name,
        eventCount: p.eventCount,
        lastDate: p.lastDate,
        people: [...p.people.values()].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => String(b.lastDate).localeCompare(String(a.lastDate)));
    return { places };
  },

  suggestPlaces: async ({ q = "", personIds = [] } = {}) => {
    const { events } = loadStore();
    const raw = String(q || "");
    const comma = raw.lastIndexOf(",");
    const query = (comma >= 0 ? raw.slice(comma + 1) : raw).trim().toLowerCase();
    const ids = [...new Set((personIds || []).map(String).filter(Boolean))];
    const ranked = new Map();

    for (const e of events) {
      const overlap = ids.length
        ? (e.personIds || []).some((id) => ids.includes(id))
        : false;
      for (const name of e.places || []) {
        const prev = ranked.get(name) || { name, score: 0, lastDate: null };
        prev.score += overlap ? 1000 : 1;
        if (!prev.lastDate || e.date > prev.lastDate) prev.lastDate = e.date;
        ranked.set(name, prev);
      }
    }

    let list = [...ranked.values()];
    if (query) list = list.filter((p) => p.name.toLowerCase().includes(query));
    list.sort((a, b) => b.score - a.score || String(b.lastDate).localeCompare(String(a.lastDate)));
    return { places: list.slice(0, 12).map((p) => p.name) };
  },

  logout: async () => {
    clearGuestSession();
    return { ok: true };
  },
};
